<script setup>
  import { ref, computed, useTemplateRef, watch, watchEffect } from 'vue';
  import TideGraphMarkerValueLabel from '@/components/TideGraphMarkerValueLabel.vue';
  import TideGraphMarkerTimeLabel from '@/components/TideGraphMarkerTimeLabel.vue';
  import TideGraphSolunarMarker from '@/components/TideGraphSolunarMarker.vue';
  import TideGraphMarker from '@/components/TideGraphMarker.vue';
  import TideGraphNowMarker from '@/components/TideGraphNowMarker.vue';
  import TideGraphTooltip from '@/components/TideGraphTooltip.vue';
  import TideGraphUnitDisplay from '@/components/TideGraphUnitDisplay.vue';
  import { format } from 'date-fns'
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { TZDate, tzOffset } from "@date-fns/tz";

  const graphContainer = useTemplateRef('graph-container');

  const props = defineProps({
    graphHeight: {
      type: Number,
      required: false,
    },
    tideData: {
      type: Array,
      required: true
    },
    tideSpotType: {
      type: String,
      required: true
    },
    tideSpotTimezone: {
      type: String,
      required: true
    },
    tideSpotTimezoneOffset: {
      type: Number,
      required: true
    },
    tideDataMax: {
      type: Number,
      default: null
    },
    tideEvents: {
      type: Array,
      required: true
    },
    solunarEvents: {
      type: Array,
      required: true
    },
    tideUnits: {
      type: String,
      required: false
    }
  });
  
  const isDesktop = useMediaQuery('(min-width: 840px)');

  const tideFillColor = {
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(206, 94%, 87%, 1.0)'],
      [1, 'hsla(206, 94%, 87%, 0)']
    ]
  };

  const currentsFillColor = {
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(286, 92%, 86%, 1.0)'],
      [0.5, 'hsla(286, 92%, 86%, 0)'],
      [1, 'hsla(286, 92%, 86%, 1.0)'],
    ]
  };

  const graphColorInfo = {
    tide: {
      color: 'hsla(211, 86%, 35%, 1.0)',
      fillColor: tideFillColor
    },
    currents: {
      color: 'hsla(287, 100%, 35%, 1.0)',
      fillColor: currentsFillColor
    }
  } 

  const graphInitialized = ref(false);
  const graph = ref(null);
  const nowTideValue = ref(null);
  const tooltipPoint = ref(null);
  const tooltipPointTimezoneOffset = ref(null);
  const tooltipVisible = ref(false);
  const tooltipXPosition = ref(0);
  const nowMarkerPosition = ref({ x: 0, y: 0 });
  const nightBlocks = ref([]);

  const tideData = ref([])
  // const tideData = computed(() => JSON.parse(JSON.stringify(props.tideData)));
  const tideSpotType = computed(() => props.tideSpotType);
  const lineColor = computed(() => graphColorInfo[tideSpotType.value]?.color);
  const fillColor = computed(() => graphColorInfo[tideSpotType.value]?.fillColor);
  const graphUnit = computed(() => props.tideUnits);
  const now = computed(() => Date.now());
  const height = computed(() => {
    if (props.graphHeight) return `${props.graphHeight}px`;

    return isDesktop.value || tideSpotType.value === 'currents' ? '400px' : '325px';
  });

  const tideEventGraphPoints = computed(() => {
    if (!graph.value) return [];

    return props.tideEvents.map(event => {
      const eventDateTime = new Date(event.dateTime).getTime();
      const graphPoint = graph.value.series[0].points.find(point => point.x === eventDateTime);

      if (graphPoint) {
          graphPoint.tideEventName = event.eventName;
          graphPoint.tideEventValue = event.eventValue;
          graphPoint.tideEventIconName = event.iconName;
          graphPoint.tideEventIsNext = event.isNext;
          graphPoint.tideEventFormattedTime = event.timeFormatted;
          return graphPoint;
      }
      
      return null;
    }).filter(point => point !== null);
  });

  const solunarEventGraphPoints = computed(() => {
    if (!graph.value) return [];

    return props.solunarEvents.map(event => {
      if (!event.eventName.toLowerCase().includes('sunset') && !event.eventName.toLowerCase().includes('sunrise') && !event.eventName.toLowerCase().includes('moonset') && !event.eventName.toLowerCase().includes('moonrise')) return null;

      const eventDateTime = new Date(event.dateTime).getTime();
      const iconName  = event.eventName.toLowerCase().replaceAll(',', '').replaceAll(' ', '-');
      const graphPoint = graph.value.series[0].points.find(point => point.x === eventDateTime);

      if (graphPoint) {
          graphPoint.tideEventName = event.eventName;
          graphPoint.tideEventValue = event.eventValue;
          graphPoint.tideEventIconName = iconName;
          return graphPoint;
      }
      
      return null;
    }).filter(point => point !== null);
  });

  function initializeGraph () {           
    graph.value = Highcharts.chart(graphContainer.value, {
      chart: {
          type: 'areaspline',
          backgroundColor: 'transparent',
          borderWidth: 0,
          height: height.value,
          marginTop: 0,
          marginRight: 0,
          spacingBottom: 5,
          events: {
            load: function () {
              graph.value = this;
            },
            render: function () {
              drawNightBlocks();
              updateNowPosition();
            }
          }
        },
      credits: {
          enabled: false,
      },
      legend: {
          enabled: false,
      },      
      xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            day: '%l %p',
            hour: '%l %p',
            minute: '%l:%M %p',
            millisecond: '%l:%M %p',
          },
          endOnTick: true,
          lineColor: 'hsl(0, 0%, 90%, 1)',
          gridLineWidth: 1,
          labels: {
            enabled: true,
            useHTML: true,
            formatter: function () {
                const label = this.axis.defaultLabelFormatter.call(this);
                const isTimeLabel = label.indexOf('AM') > -1 || label.indexOf('PM') > -1;

                if (!isTimeLabel) {
                  return null;
                }

                const dayPeriod = label.indexOf('AM') > -1 ? 'A' : 'P';
                const time = label.split(' ')[0];

                return `<span class="jwx-graph__label-text">${time}${dayPeriod}</span>`;
            },
            step: 1,
          },
          minPadding: 0,
          maxPadding: 0,
          offset: 32,       
          showFirstLabel:  false,
          showLastLabel:  false,
          tickInterval: isDesktop.value ? 3600 * 1000 : 3600 * 1000 * 2, // one hour or two hours
          tickLength: 0,
      },
      yAxis: {
        max: props.tideDataMax && isDesktop.value ? props.tideDataMax + 2 : null,
        maxPadding: !isDesktop.value ? .5 : null,
        labels: {
          distance: 0,
          x: -10,
        },
        lineWidth:0,
        showLastLabel:  false,        
        tickLength: 0,
        title: {
          enabled: false
        },
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: {
              opacity: 1
            }
          },
          point: {
            events: {
              mouseOver: function (e) {
                tooltipPoint.value = e.target;
                tooltipPointTimezoneOffset.value = tideData.value[e.target.index][2]
                updateTooltipPosition();
                tooltipVisible.value = true;

                return false;
              },
              mouseOut: function () {
                tooltipVisible.value = false;

                if (graph.value.tooltipPointer) {
                  graph.value.tooltipPointer.destroy();
                  graph.value.tooltipPointer = null;
                }

                return false;
              }
          }
          }
        }
      },      
      series: [{
        name: 'Tide Data',
        data: tideData.value,
        color: lineColor.value,
        fillColor: fillColor.value,
        lineWidth: 4,     
        pointStart: tideData.value?.length ? tideData.value[0][0] : null,        
        zoneAxis: 'x',
        zones: [{
          value: Date.now(),
          color: 'hsla(0, 0%, 61%, 1.0)'
        }, {
          color: lineColor.value,
        }]          
      }],
      time: {
          useUTC: true,
          getTimezoneOffset: function (timestamp) {
            const date = new Date(timestamp);
            const offset = tzOffset(props.tideSpotTimezone, date) * -1 // Example timezone
            return offset            
          }
        },
      title: {
        text: null,
      },
      tooltip: {
        enabled: false,
      },
    });
    
    graphInitialized.value = true;
  }

  function updateNowPosition() {
    const [beforeIndex, afterIndex] = getBeforeAndAfterNowIndexes();

    if (beforeIndex === -1 || afterIndex === -1) { 
      nowMarkerPosition.value = null;
      return;
    }
    
    const beforePoint = graph.value.series[0].points[beforeIndex];
    const afterPoint = graph.value.series[0].points[afterIndex];

    if (!beforePoint || !afterPoint) {
      nowMarkerPosition.value = null;
      return;
    }

    const nowBeforeTimeDelta = now.value - beforePoint.x;
    const currentTimePeriodLength = afterPoint.x - beforePoint.x;
    const currentTimePeriodCompleteFactor = nowBeforeTimeDelta / currentTimePeriodLength;
    const interpolatedYValue = ((afterPoint.y - beforePoint.y) * currentTimePeriodCompleteFactor) + beforePoint.y;
    const timezoneOffset = tideData.value[beforeIndex][2]

    graph.value.series[0].addPoint([now.value, interpolatedYValue, timezoneOffset], false);
    nowTideValue.value = interpolatedYValue;

    const x = graph.value.xAxis[0].toPixels(now.value) - graph.value.plotLeft;
    const y = graph.value.series[0].yAxis.toPixels(interpolatedYValue);

    nowMarkerPosition.value = { x, y };
  }

  function drawNightBlocks() {
    if (nightBlocks.value.length) {
      nightBlocks.value.forEach(block => block?.destroy());
      nightBlocks.value = [];
    }

    const sunEvents = solunarEventGraphPoints.value.filter(event => event.tideEventName.toLowerCase().includes('sun'));
    const y = graph.value.series[0].yAxis.top;
    const height = graph.value.series[0].yAxis.height;
    const graphPoints = graph.value.series[0].points;
    const startXValue = graphPoints[0].x;
    const endXValue = graphPoints[graphPoints.length - 1].x;
    let x1 = graph.value.xAxis[0].toPixels(startXValue);
    let x2 = null;
    let width = 0;
    
    for (let i = 0; i < sunEvents.length; i++) {
      const event = sunEvents[i];

      if (event.tideEventName.toLowerCase().includes('sunrise')) {
        if (i === 0) {
          x1 = graph.value.xAxis[0].toPixels(startXValue);
          x2 = graph.value.xAxis[0].toPixels(event.x);
        } else {
          continue;
        }
      }

      if (event.tideEventName.toLowerCase().includes('sunset')) {
        if (i === sunEvents.length - 1) {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(endXValue);
        } else {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(sunEvents[i + 1].x);
        }
      }

      width = x2 - x1;

      const nightBlock = graph.value.renderer.rect(x1, y, width, height)
        .attr({
          fill: 'hsla(0, 0%, 90%, 0.4)',
          zIndex: 1
        })
        .add();

      nightBlocks.value.push(nightBlock);
    }
  }

  function updateTooltipPosition() {
    tooltipXPosition.value = graph.value.xAxis[0].toPixels(tooltipPoint.value.x);
  }

  async function updateTideData(tideData) {
    if (!graph.value || !tideData) return;

    graph.value.series[0].remove(false);

    graph.value.addSeries({
      data: tideData,
      color: lineColor.value,
      fillColor: fillColor.value,
      lineWidth: 4,
      zoneAxis: 'x',
      zones: [{
        value: now.value,
        color: 'hsla(0, 0%, 61%, 1.0)'
      }, {
        color: lineColor.value,
      }]
    }, false);

    graph.value.yAxis[0].options.max = props.tideDataMax ? props.tideDataMax + 2 : null;
    // graph.value.update({ time: {
    //       timezoneOffset: props.tideSpotTimezoneOffset * -60 // Highcharts uses opposite sign for timezone offset
    //     } 
    // }, false);

    graph.value.redraw();
  }

  function getBeforeAndAfterNowIndexes() {
    if (!graph.value) return { before: null, after: null };

    const afterIndex = graph.value.series[0].data.findIndex(element => element.x > now.value);
    let beforeIndex = null;
    
    graph.value.series[0].data.forEach((element, i) => {
      if (element.x < now.value) beforeIndex = i;
    });

    return [beforeIndex, afterIndex];
  }

  watchEffect(async() => {
    if (graphContainer.value && !graphInitialized.value && props.tideData) {
      initializeGraph();
    }
  });

  watch(tideData, (newValue) => {
    updateTideData(newValue);
  });

  watchEffect(() => {
    tideData.value = JSON.parse(JSON.stringify(props.tideData))
  })

  watch(tideSpotType, (newValue) => {
    graph.value.redraw();
  });

</script>

<template>
  <div ref="graph-container" id="jwx-tide-graph-container" class="jwx-tide-graph__container" :style="{'--jwx-tide-graph-y-axis-width': `${graph ? graph.plotLeft: 0}px`}"></div>
  <div v-if="graph" class="jwx-tide-graph__setup-block">
    <TideGraphUnitDisplay 
      v-if="graph"
      :units="graphUnit"
      :graph="graph"
    />
    <TideGraphTooltip
      v-if="tooltipVisible"
      :unit="graphUnit"
      :positionX="tooltipXPosition"
      :displayValue="format(new TZDate(tooltipPoint.x, tooltipPointTimezoneOffset), 'h:mm a')"
      :lineColor="lineColor"
      :point="tooltipPoint"
      :graph="graph"
    />
    <TideGraphMarker
      v-for="marker in tideEventGraphPoints" 
      :key="`${marker.tideEventValue}-${marker.plotX}`"
      :value="marker.x" 
      :position="{ x: marker.plotX, y: marker.plotY }"
      :lineColor="lineColor"
      :isNext="marker.tideEventIsNext"
      :graph="graph"
    />
    <TideGraphMarkerValueLabel 
      v-for="marker in tideEventGraphPoints" 
      :key="`${marker.tideEventValue}-${marker.plotX}`"
      :value="marker.tideEventValue" 
      :iconName="marker.tideEventIconName" 
      :position="{ x: marker.plotX, y: marker.plotY }"
      :graph="graph"
    />
    <TideGraphMarkerTimeLabel 
      v-for="marker in tideEventGraphPoints" 
      :key="`${marker.tideEventTime}-${marker.plotX}`"
      :value="marker.x" 
      :displayValue="marker.tideEventFormattedTime"
      :iconName="marker.tideEventIconName" 
      :position="{ x: marker.plotX, y: marker.plotY }"
      :graph="graph"
    />
    <TideGraphSolunarMarker 
      v-for="marker in solunarEventGraphPoints" 
      :key="`${marker.tideEventTime}-${marker.plotX}`"
      :value="marker.x" 
      :iconName="marker.tideEventIconName" 
      :position="{ x: marker.plotX, y: marker.plotY }"
      :graph="graph"
    />
    <TideGraphNowMarker
      v-if="nowMarkerPosition"
      :position="nowMarkerPosition"
      :graph="graph"
    />
  </div>
</template>